/**
 * URL search params: the contract between the URL and a screen's state.
 *
 * The router parses the query string into `RawSearch` — plain strings, with a
 * repeated key becoming an array (`?os=ios&os=android` → `{ os: ['ios','android'] }`),
 * the same encoding apps/web and FastAPI use. No JSON coercion: `app_id=123`
 * stays the string "123".
 *
 * A route turns RawSearch into typed state in `validateSearch`, using the
 * `param` readers below. Readers accept `unknown` because TanStack Router also
 * feeds `validateSearch` the typed object passed to `navigate({ search })`.
 *
 *   const searchSchema = (raw: RawSearch) => ({
 *     date: param.dateRange(raw, 'date_from', 'date_to'),
 *     os: param.enumList(raw, 'os', ['ios', 'android'] as const),
 *     min_impressions: param.number(raw, 'min_impressions', { min: 0 }),
 *   });
 *   createRoute({ ..., validateSearch: defineSearch(searchSchema) })
 *   // useSearch({ from: '/_shell/performance' }) → fully typed state
 *   // <Link to="/performance" search={{ os: ['ios'] }}> → only the keys you set
 *
 * `stringifySearch` drops undefined, null, '' and empty arrays, so a default
 * value never lingers in the URL.
 */

export type RawSearch = Record<string, unknown>;

export const parseSearch = (searchStr: string): Record<string, string | string[]> => {
  const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr);
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of params) {
    const existing = out[key];
    if (existing === undefined) out[key] = value;
    else out[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
  }
  return out;
};

const scalar = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return null;
};

export const stringifySearch = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    const values = Array.isArray(value) ? (value as unknown[]) : [value];
    for (const item of values) {
      const text = scalar(item);
      if (text !== null && text !== '') params.append(key, text);
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const first = (value: unknown): unknown => (Array.isArray(value) ? (value as unknown[])[0] : value);
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const isValidDate = (value: string): boolean => {
  if (!DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const param = {
  /** A single non-empty string (first value if repeated). */
  string(raw: RawSearch, key: string): string | undefined {
    const text = scalar(first(raw[key]));
    return text ? text : undefined;
  },

  /** De-duplicated, non-empty strings; [] when absent. */
  list(raw: RawSearch, key: string): string[] {
    const value = raw[key];
    const values = Array.isArray(value) ? (value as unknown[]) : value === undefined ? [] : [value];
    return [...new Set(values.map(scalar).filter((v): v is string => v !== null && v.trim() !== ''))];
  },

  /** One of `allowed`, else undefined. */
  enumValue<const T extends string>(raw: RawSearch, key: string, allowed: readonly T[]): T | undefined {
    const text = param.string(raw, key);
    return text !== undefined && (allowed as readonly string[]).includes(text) ? (text as T) : undefined;
  },

  /** The values of `key` that are in `allowed`, de-duplicated. */
  enumList<const T extends string>(raw: RawSearch, key: string, allowed: readonly T[]): T[] {
    return param.list(raw, key).filter((v): v is T => (allowed as readonly string[]).includes(v));
  },

  /** A finite number within bounds, else undefined. */
  number(raw: RawSearch, key: string, bounds: { min?: number; max?: number; integer?: boolean } = {}): number | undefined {
    const text = scalar(first(raw[key]));
    if (text === null || text.trim() === '') return undefined;
    const value = Number(text);
    if (!Number.isFinite(value)) return undefined;
    if (bounds.integer && !Number.isInteger(value)) return undefined;
    if (bounds.min !== undefined && value < bounds.min) return undefined;
    if (bounds.max !== undefined && value > bounds.max) return undefined;
    return value;
  },

  /** True only when the value equals `onValue` (default "1"); e.g. `active=24` uses '24'. */
  flag(raw: RawSearch, key: string, onValue = '1'): boolean {
    return param.string(raw, key) === onValue || first(raw[key]) === true;
  },

  /** A valid calendar day "YYYY-MM-DD", else undefined. */
  date(raw: RawSearch, key: string): string | undefined {
    const text = param.string(raw, key);
    return text !== undefined && isValidDate(text) ? text : undefined;
  },

  /** Both dates valid and from ≤ to — otherwise BOTH are ignored (parity §0). */
  dateRange(raw: RawSearch, fromKey: string, toKey: string): { from: string; to: string } | undefined {
    const from = param.date(raw, fromKey);
    const to = param.date(raw, toKey);
    return from && to && from <= to ? { from, to } : undefined;
  },
};

/**
 * A route's search validator, in the Standard Schema shape TanStack Router
 * understands: reading the URL yields the full typed state `T`; navigating
 * accepts `Partial<T>`, so a link names only the keys it sets and every other
 * key falls back to the schema's default.
 */
export type SearchValidator<T> = {
  readonly '~standard': {
    readonly version: 1;
    readonly vendor: 'lemon-app';
    readonly validate: (value: unknown) => { readonly value: T };
    readonly types?: { readonly input: Partial<T>; readonly output: T };
  };
};

const asRecord = (value: unknown): RawSearch =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as RawSearch) : {};

/** Wrap a schema function (built from `param.*`) as a route's `validateSearch`. */
export const defineSearch = <T>(schema: (raw: RawSearch) => T): SearchValidator<T> => ({
  '~standard': {
    version: 1,
    vendor: 'lemon-app',
    validate: (value: unknown) => ({ value: schema(asRecord(value)) }),
  },
});
